import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  FiSearch, FiBell, FiRefreshCw, 
  FiUser, FiFileText, FiVideo, FiActivity, 
  FiFolder, FiSettings, FiGrid, FiArrowRight, FiShield, FiCreditCard, FiBarChart2
} from "react-icons/fi";
import { adminApi } from "../../services/adminApi";
import { liveSessionAPI, practiceVideoAPI, blogAPI, notificationAPI, dietPlanAPI } from "../../services/api";

const T = {
  olive: "#6B8A5A",
  olive2: "#8BA47A",
  orange: "#F29A0C",
  orange2: "#E97818",
  border: "rgba(239,229,216,0.8)",
  muted: "#AAA",
  red: "#E24B4A"
};

export default function AdminTopbar({ onRefresh }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);

  // Search source states
  const [sessions, setSessions] = useState([]);
  const [videos, setVideos] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);

  // Fetch search sources and online count dynamically across all system modules
  const fetchTopbarStats = async () => {
    try {
      const [sessRes, vidRes, blogRes, userRes, pkgRes, notifRes, dietRes, statsRes] = await Promise.allSettled([
        liveSessionAPI.getSessions(),
        practiceVideoAPI.getVideos(),
        blogAPI.getBlogs(),
        adminApi.getUsers({ pageSize: 1000 }),
        adminApi.getPackages(),
        notificationAPI.getAdminNotificationHistory(),
        dietPlanAPI.getAdminPlans(),
        adminApi.getDashboardStats()
      ]);

      if (sessRes.status === "fulfilled" && sessRes.value) {
        const v = sessRes.value;
        const list = v.data?.data || v.data || (Array.isArray(v) ? v : []);
        setSessions(Array.isArray(list) ? list : []);
      }
      if (vidRes.status === "fulfilled" && vidRes.value) {
        const v = vidRes.value;
        const list = v.data?.data || v.data || (Array.isArray(v) ? v : []);
        setVideos(Array.isArray(list) ? list : []);
      }
      if (blogRes.status === "fulfilled" && blogRes.value) {
        const v = blogRes.value;
        const list = v.data?.data || v.data || (Array.isArray(v) ? v : []);
        setBlogs(Array.isArray(list) ? list : []);
      }
      if (userRes.status === "fulfilled" && userRes.value) {
        const v = userRes.value;
        const list = v.data?.users || v.data?.Users || v.users || v.Users || (Array.isArray(v.data) ? v.data : (Array.isArray(v) ? v : []));
        setUsers(Array.isArray(list) ? list : []);
      }
      if (pkgRes.status === "fulfilled" && pkgRes.value) {
        const v = pkgRes.value;
        const list = v.data?.data || v.data?.packages || v.data || (Array.isArray(v) ? v : []);
        setPackages(Array.isArray(list) ? list : []);
      }
      if (notifRes.status === "fulfilled" && notifRes.value) {
        const v = notifRes.value;
        const list = v.data?.data || v.data || (Array.isArray(v) ? v : []);
        setNotifications(Array.isArray(list) ? list : []);
      }
      if (dietRes.status === "fulfilled" && dietRes.value) {
        const v = dietRes.value;
        const list = v.data?.data || v.data || (Array.isArray(v) ? v : []);
        setDietPlans(Array.isArray(list) ? list : []);
      }
      // Populate online user count dynamically from active database users
      if (statsRes.status === "fulfilled" && statsRes.value) {
        const v = statsRes.value;
        const stats = v.data || v;
        const count = stats.onlineUsersCount ?? stats.OnlineUsersCount ?? 0;
        setOnlineCount(count);
      }
    } catch (err) {
      console.error("Failed to load end-to-end search sources in topbar:", err);
    }
  };

  useEffect(() => {
    fetchTopbarStats();
  }, [location.pathname]);

  // Live dynamic backend user search on typing
  useEffect(() => {
    if (!searchQuery.trim()) {
      setLiveUsers([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await adminApi.getUsers({ search: searchQuery, pageSize: 20 });
        const raw = res.data?.users ?? res.data?.Users ?? res.users ?? res.Users ?? [];
        setLiveUsers(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.warn("Live user search error:", err);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Compute page title based on path
  const pageTitle = useMemo(() => {
    const path = location.pathname;
    if (path.includes("/dashboard")) return "Admin Dashboard";
    if (path.includes("/usermanagement") || path === "/admin/users") return "User Management";
    if (/\/(highpriorityuser|high-priority-users|priority)/.test(path)) return "High Priority Users";
    if (path.includes("/subscription")) return "Subscription Details";
    if (path.includes("/contentmanager")) return "Content Library Manager";
    if (path.includes("/livesession")) return "Live Classes & Studio";
    if (path.includes("/dietplan")) return "Diet Plan Editor";
    if (path.includes("/notifications")) return "Broadcaster Panel";
    if (path.includes("/reports")) return "Analytics & Audit Logs";
    if (path.includes("/queries") || path.includes("/user-queries")) return "User Queries";
    if (path.includes("/settings")) return "Global Platform Configuration";
    return "Admin Console";
  }, [location.pathname]);

  // Static site pages & section listings to search
  const ADMIN_PAGES = [
    { name: "Admin Dashboard", path: "/admin/dashboard", desc: "Overview, quick analytics & KPIs", icon: FiGrid },
    { name: "User Management", path: "/admin/usermanagement", desc: "View streaks, roles & activation", icon: FiUser },
    { name: "High Priority Users", path: "/admin/high-priority-users", desc: "View at-risk members, health notes & alerts", icon: FiShield },
    { name: "Subscriptions & Packages", path: "/admin/subscriptions", desc: "Plans, tiers & payment ledger verification", icon: FiCreditCard },
    { name: "Content Library Manager", path: "/admin/contentmanager", desc: "Upload and index practice videos & blogs", icon: FiVideo },
    { name: "Live Session Studio", path: "/admin/livesessions", desc: "Schedule online classes & categories", icon: FiActivity },
    { name: "Diet Plan Editor", path: "/admin/dietplans", desc: "Manage macro schedules & recommendations", icon: FiFolder },
    { name: "Notifications Panel", path: "/admin/notifications", desc: "Broadcast history & custom scheduler", icon: FiBell },
    { name: "Analytics & Audit Reports", path: "/admin/reports&analytics", desc: "View audit logs, revenue charts & export data", icon: FiBarChart2 },
    { name: "Global Settings & Configuration", path: "/admin/settings", desc: "Manage appearance, background music, branding & parameters", icon: FiSettings }
  ];

  // Dynamic End-to-End Search Categorization across ALL Admin Data
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();

    // 1. System Pages & Navigation
    const matchedPages = ADMIN_PAGES.filter(p => 
      p.name.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q)
    );

    // 2. High Priority & At-Risk Users
    const combinedUsersList = [...liveUsers, ...users];
    const uniqueUserMap = new Map();
    combinedUsersList.forEach(u => {
      const key = u.id || u.email || u.userName;
      if (key && !uniqueUserMap.has(key)) {
        uniqueUserMap.set(key, u);
      }
    });
    const allUsersArr = Array.from(uniqueUserMap.values());

    const matchedHighPriority = allUsersArr.filter(u => {
      const isRisk = u.isHighPriority || u.isActive === false || Boolean(u.healthFlags || u.notes);
      const isQueryMatch = (u.fullName || "")?.toLowerCase().includes(q) || 
                           (u.userName || "")?.toLowerCase().includes(q) || 
                           (u.email || "")?.toLowerCase().includes(q) ||
                           q.includes("priority") || q.includes("risk") || q.includes("health") || q.includes("flag");
      return isRisk && isQueryMatch;
    }).slice(0, 5).map(u => ({
      name: u.fullName || u.userName || u.email,
      desc: `High Priority | Email: ${u.email || "N/A"} | Risk Status: ${u.isActive ? "Flagged" : "Inactive"}`,
      path: `/admin/high-priority-users?search=${encodeURIComponent(u.email || u.fullName || u.userName)}`,
      icon: FiShield
    }));

    // 3. User Management & Members
    const matchedUsers = allUsersArr.filter(u => 
      (u.fullName || "")?.toLowerCase().includes(q) || 
      (u.userName || "")?.toLowerCase().includes(q) || 
      (u.email || "")?.toLowerCase().includes(q) ||
      (u.role || "")?.toLowerCase().includes(q) ||
      (u.userTier || u.tier || "")?.toLowerCase().includes(q) ||
      (u.phoneNumber || "")?.toLowerCase().includes(q)
    ).slice(0, 8).map(u => ({
      name: u.fullName || u.userName || u.email,
      desc: `Member | Email: ${u.email || "N/A"} | Role: ${u.role || "User"} | Tier: ${u.userTier || u.tier || "Free"}`,
      path: `/admin/usermanagement?search=${encodeURIComponent(u.email || u.fullName || u.userName)}`,
      icon: FiUser
    }));

    // 4. Subscriptions & Packages
    const matchedPackages = packages.filter(p => 
      p.name?.toLowerCase().includes(q) || 
      p.tier?.toLowerCase().includes(q) || 
      String(p.price || "").includes(q)
    ).slice(0, 4).map(p => ({
      name: p.name || `${p.tier} Plan`,
      desc: `Subscription | Tier: ${p.tier || "General"} | Price: ₹${p.price || 0}`,
      path: `/admin/subscriptions?search=${encodeURIComponent(p.name || p.tier)}`,
      icon: FiCreditCard
    }));

    // 5. Content Library (Practice Videos & Blogs)
    const matchedVideos = videos.filter(v => 
      v.title?.toLowerCase().includes(q) || 
      v.description?.toLowerCase().includes(q) ||
      v.category?.toLowerCase().includes(q) ||
      v.instructor?.toLowerCase().includes(q)
    ).map(v => ({
      name: v.title,
      desc: `Practice Video | Category: ${v.category || "General"} | Instructor: ${v.instructor || "N/A"}`,
      path: `/admin/contentmanager?search=${encodeURIComponent(v.title)}`,
      icon: FiVideo
    }));

    const matchedBlogs = blogs.filter(b => 
      b.title?.toLowerCase().includes(q) || b.summary?.toLowerCase().includes(q)
    ).map(b => ({
      name: b.title,
      desc: `Blog Post | Category: ${b.category || "General"}`,
      path: `/admin/contentmanager?search=${encodeURIComponent(b.title)}`,
      icon: FiFileText
    }));

    const matchedContent = [...matchedVideos, ...matchedBlogs].slice(0, 6);

    // 6. Live Sessions & Classes
    const matchedSessions = sessions.filter(s => 
      s.title?.toLowerCase().includes(q) || 
      s.instructorName?.toLowerCase().includes(q) ||
      s.category?.toLowerCase().includes(q) ||
      s.status?.toLowerCase().includes(q)
    ).slice(0, 5).map(s => ({
      name: s.title,
      desc: `Live Session | Instructor: ${s.instructorName || "N/A"} | Status: ${s.status || "Active"}`,
      path: `/admin/livesessions?search=${encodeURIComponent(s.title)}`,
      icon: FiActivity
    }));

    // 7. Diet Plans
    const matchedDietPlans = dietPlans.filter(d => 
      d.planName?.toLowerCase().includes(q) || 
      d.dietType?.toLowerCase().includes(q) ||
      d.region?.toLowerCase().includes(q) ||
      d.calorieGoal?.toLowerCase().includes(q)
    ).slice(0, 4).map(d => ({
      name: d.planName || `${d.dietType} Plan`,
      desc: `Diet Plan | Type: ${d.dietType || "General"} | Region: ${d.region || "All"}`,
      path: `/admin/dietplans?search=${encodeURIComponent(d.planName || d.dietType)}`,
      icon: FiFolder
    }));

    // 8. Broadcaster Notifications
    const matchedNotifs = notifications.filter(n => 
      n.title?.toLowerCase().includes(q) || 
      n.message?.toLowerCase().includes(q) ||
      n.audience?.toLowerCase().includes(q)
    ).slice(0, 4).map(n => ({
      name: n.title,
      desc: `Notification | Target: ${n.audience} | Status: ${n.status || "Sent"}`,
      path: `/admin/notifications?search=${encodeURIComponent(n.title)}`,
      icon: FiBell
    }));

    // 9. Analytics & Audit Reports
    const matchedReports = (q.includes("report") || q.includes("analytic") || q.includes("audit") || q.includes("revenue") || q.includes("log") || q.includes("chart") || q.includes("metric"))
      ? [{
          name: "Analytics & Audit Reports",
          desc: "View platform revenue, audit logs, active user trends & export reports",
          path: "/admin/reports&analytics",
          icon: FiBarChart2
        }]
      : [];

    // 10. Global Settings & Configuration
    const matchedSettings = (q.includes("setting") || q.includes("config") || q.includes("appearance") || q.includes("music") || q.includes("audio") || q.includes("branding") || q.includes("logo") || q.includes("parameter"))
      ? [{
          name: "Global Settings & Configuration",
          desc: "Manage appearance, background music, branding, roles & system parameters",
          path: "/admin/settings",
          icon: FiSettings
        }]
      : [];

    const results = [
      { category: "System Pages & Navigation", icon: FiGrid, list: matchedPages },
      { category: "High Priority & At-Risk Users", icon: FiShield, list: matchedHighPriority },
      { category: "User Management & Members", icon: FiUser, list: matchedUsers },
      { category: "Subscriptions & Packages", icon: FiCreditCard, list: matchedPackages },
      { category: "Content Library & Practice Videos", icon: FiVideo, list: matchedContent },
      { category: "Live Sessions & Studio", icon: FiActivity, list: matchedSessions },
      { category: "Diet Plan Editor", icon: FiFolder, list: matchedDietPlans },
      { category: "Broadcaster Notifications", icon: FiBell, list: matchedNotifs },
      { category: "Analytics & Audit Reports", icon: FiBarChart2, list: matchedReports },
      { category: "Global Settings & Configuration", icon: FiSettings, list: matchedSettings }
    ].filter(g => g.list.length > 0);

    return results;
  }, [searchQuery, sessions, videos, blogs, users, liveUsers, packages, notifications, dietPlans]);

  const handleRefreshClick = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      window.location.reload();
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: -12 }} 
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-2 sm:px-8 mx-0 sm:mx-8 mb-4 sm:mb-8 pb-4 border-b border-zinc-200/50"
      style={{ zIndex: 100, position: "relative" }}
    >
      {/* Title Block */}
      <div>
        <h1 className="font-black text-[#1F1F1F]"
          style={{ fontFamily: "'Poppins', sans-serif", fontSize: 24, letterSpacing: "-0.8px" }}>
          {pageTitle}
        </h1>
        <p className="text-[11px] mt-0.5 flex items-center gap-2" style={{ color: T.muted }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold"
            style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", color: "#16A34A" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block animate-pulse" />
            {onlineCount} online
          </span>
        </p>
      </div>

      {/* Global Action Tools */}
      <div className="flex items-center gap-3 relative flex-wrap sm:flex-nowrap">
        
        {/* End-to-End Omni Search Box */}
        <div className="relative w-full sm:w-auto">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-[14px] transition-all duration-300 w-full sm:w-auto"
            style={{ background: "rgba(255,255,255,0.92)", border: `1px solid ${T.border}`, boxShadow: "0 4px 12px rgba(0,0,0,0.01)" }}>
            <FiSearch size={14} className="text-zinc-400 shrink-0" />
            <input 
              placeholder="Search users, email, plans, sessions..."
              value={searchQuery}
              onFocus={() => setDropdownOpen(true)}
              onChange={e => { setSearchQuery(e.target.value); setDropdownOpen(true); }}
              className="bg-transparent border-none outline-none text-[13px] text-[#1F1F1F] w-full sm:w-[260px]"
              style={{ fontFamily: "'Inter', sans-serif" }} 
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="border-none bg-transparent cursor-pointer text-zinc-400 hover:text-zinc-600 outline-none text-xs font-bold"
              >
                Clear
              </button>
            )}
          </div>

          {/* Omni Search Glass Dropdown */}
          <AnimatePresence>
            {dropdownOpen && searchQuery.trim() && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                <motion.div 
                  initial={{ opacity: 0, y: 10, scale: 0.98 }} 
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  className="ad-search-drop absolute top-12 left-0 w-[440px] max-h-[420px] bg-white/98 backdrop-blur-md rounded-[20px] border shadow-2xl overflow-y-auto z-50 p-3.5 text-left" 
                  style={{ borderColor: T.border }}
                >
                  {searchResults && searchResults.length === 0 ? (
                    <div className="text-center py-6">
                      <FiSearch size={22} className="mx-auto text-zinc-300 mb-2" />
                      <p className="text-[13px] text-zinc-500 font-bold">No matching records found</p>
                      <p className="text-[10px] text-zinc-400 mt-0.5">Try searching for user emails, plan names, or titles</p>
                    </div>
                  ) : (
                    searchResults && searchResults.map(grp => (
                      <div key={grp.category} className="mb-4 last:mb-0">
                        <p className="text-[9.5px] font-black text-zinc-500 uppercase tracking-widest px-2.5 mb-1.5 flex items-center gap-1.5">
                          {grp.icon && <grp.icon size={11} className="text-amber-700 shrink-0" />}
                          <span>{grp.category}</span>
                        </p>
                        <div className="space-y-0.5">
                          {grp.list.map((item, idx) => (
                            <div 
                              key={idx} 
                              onClick={() => { 
                                setDropdownOpen(false); 
                                setSearchQuery(""); 
                                navigate(item.path);
                              }}
                              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-amber-50/80 cursor-pointer transition-all border border-transparent hover:border-amber-200/60 group"
                            >
                              <div className="w-8 h-8 rounded-[10px] flex items-center justify-center bg-zinc-100 text-zinc-600 group-hover:bg-amber-100 group-hover:text-amber-800 transition-colors shrink-0">
                                <item.icon size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[12.5px] font-bold text-[#1F1F1F] truncate group-hover:text-amber-900">{item.name}</p>
                                <p className="text-[10.5px] text-zinc-500 truncate">{item.desc}</p>
                              </div>
                              <FiArrowRight size={13} className="text-[#6B8A5A] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Global Notifications Bell Shortcut */}
        <div 
          onClick={() => navigate("/admin/notifications")}
          className="relative w-10 h-10 flex items-center justify-center rounded-[13px] cursor-pointer hover:bg-white/90 transition-all border"
          style={{ background: "rgba(255,255,255,0.92)", borderColor: T.border }}
          title="Notifications Panel"
        >
          <FiBell size={16} className="text-zinc-500" />
          <div className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full border-2 bg-[#F29A0C]" style={{ borderColor: "#FFF8EE" }} />
        </div>

        {/* Global Data Refresh Button */}
        <motion.button 
          whileHover={{ y: -2 }} 
          whileTap={{ scale: 0.97 }}
          onClick={handleRefreshClick}
          className="h-10 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-2"
          style={{
            background: `linear-gradient(135deg,${T.olive},${T.olive2})`,
            boxShadow: `0 6px 18px rgba(107,138,90,0.3)`,
            border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif"
          }}
        >
          <FiRefreshCw size={12} />
          <span>Refresh</span>
        </motion.button>
      </div>
    </motion.div>
  );
}
